import React from 'react';
import * as yup from 'yup';
import { Box, LinearProgress, Typography } from '@mui/material';
import Lottie from 'react-lottie';
import {
  useAutoPluginAuthenticationAuthorizeConnection,
  useAutoPluginConnectionConfigure,
  usePluginAuthenticationAuthorizeConnection,
  usePluginAuthenticationParameters,
  usePluginConnectionConfigure,
} from 'hooks/SparkBuilder/pluginFunction';
import {
  DynamicUIComponentFieldController,
  SparkBasicFormFooter,
  SparkForm,
} from 'sharedComponents/SparkForm';
import { SparkButton, SparkDialog } from 'sharedComponents/UI';
import type { Plugins } from 'types/Plugins';
import type { EnumValues, SliderOptions, SliderValues } from 'types/common';
import type { PluginConnections } from 'types/PluginConnections';
import type { DropdownOption } from 'types/common/DropdownOption';
import { parseJSONCleaned } from 'utilities/index';
import { evaluateInitialValue } from 'utilities/form';
import { ErrorStatusIcon, WorkSpaceIcon } from 'components/SVG';
import sparkLoadingAnimation from 'animations/sparkLoading.json';
import { useIsConnectionValid } from './utils';

const getIsMultiSelectField = (enumValue: string | undefined | null) => {
  if (!enumValue) {
    return false;
  }
  const parsedEnumValues = parseJSONCleaned<EnumValues>(enumValue);

  return parsedEnumValues?.selectionType === 'multi';
};

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: sparkLoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

type PluginAuthenticationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  pluginAuthenticationID: string | undefined;
  selectedPlugins: Plugins | undefined;
  pluginConnection: PluginConnections | null | undefined;
  onCreatePluginConnection: (pluginConnectionID: string) => void;
};
type CustomErrorType = {
  message: string;
};

function PluginAuthenticationModal({
  isOpen,
  onClose,
  pluginAuthenticationID,
  selectedPlugins,
  pluginConnection,
  onCreatePluginConnection,
}: PluginAuthenticationModalProps) {
  const [connectionError, setConnectionError] =
    React.useState<CustomErrorType | null>(null);
  const [showError, setShowError] = React.useState(false);
  const checkConnectionValidity = useIsConnectionValid();

  const formValuesRef = React.useRef<Record<string, any>>({});

  const {
    joinedPluginAuthenticationPluginParameters,
    isLoading: isAuthenticationParameterLoading,
    error: authenticationParameterError,
    remove: removePluginAuthParameter,
  } = usePluginAuthenticationParameters(pluginAuthenticationID);

  const {
    mutateAsync: authorizePluginAuthenticationConnection,
    isLoading: isAuthorizeConnectionConnectionLoading,
    error: authorizeConnectionError,
    reset: resetAuthorizeConnect,
  } = usePluginAuthenticationAuthorizeConnection();

  const {
    mutateAsync: configurePluginConnection,
    isLoading: isConnectionConfigureLoading,
    error: configureConnectionError,
    reset: resetConfigureConnection,
  } = usePluginConnectionConfigure();

  const {
    isLoading: isAutoAuthorizeConnectionLoading,
    refetch: refetchAuthorizeConnection,
    isSuccess: isAutoAuthorizeSuccess,
    error: autoAuthorizeConnectionError,
  } = useAutoPluginAuthenticationAuthorizeConnection({
    enabled: joinedPluginAuthenticationPluginParameters?.length === 0,
    authenticationConnection: {
      pluginAuthenticationID: pluginAuthenticationID,
      description: 'Test Plugin Connection Authorization',
      name: 'test',
    },
  });

  const autoConfigureConnectionCallback = React.useCallback(
    (value: string) => {
      if (!pluginConnection?.id && value) {
        onCreatePluginConnection(value);
      }
      if (isAutoAuthorizeSuccess) {
        onClose();
      }
    },
    [
      isAutoAuthorizeSuccess,
      onClose,
      onCreatePluginConnection,
      pluginConnection?.id,
    ]
  );

  const {
    isLoading: isAutoConfigureConnectionLoading,
    refetch: refetchConfigureConnection,
    error: autoConfigureConnectionError,
  } = useAutoPluginConnectionConfigure({
    enabled:
      joinedPluginAuthenticationPluginParameters?.length === 0 &&
      !isAutoAuthorizeConnectionLoading,
    connectionConfigure: {
      pluginConnectionID: pluginConnection?.id,
      ...(!pluginConnection?.id && {
        name: 'new_connection',
        description: 'new_connection',
      }),
      pluginAuthenticationID: pluginAuthenticationID,
    },
    onCreatePluginConnection: autoConfigureConnectionCallback,
  });

  const initialFormValue: any = React.useMemo(() => {
    return (
      joinedPluginAuthenticationPluginParameters?.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.parameterAttributes.key + '']: evaluateInitialValue(
            curr.parameterAttributes.defaultValue
          ),
        };
      }, {}) || {}
    );
  }, [joinedPluginAuthenticationPluginParameters]);

  const validationSchema = yup.object().shape(
    joinedPluginAuthenticationPluginParameters?.reduce((acc, curr) => {
      const { parameterAttributes } = curr || {};
      const isMultiSelectField = getIsMultiSelectField(
        parameterAttributes?.enumValues
      );
      if (isMultiSelectField) {
        return {
          ...acc,
          [curr.parameterAttributes.key + '']: parameterAttributes?.required
            ? yup
                .array(yup.string())
                .min(1)
                .required(`${parameterAttributes?.headerText} is required`)
            : yup
                .array(yup.string())
                .typeError(
                  `${parameterAttributes?.headerText} should be string`
                ),
        };
      }
      return {
        ...acc,
        [curr.parameterAttributes.key + '']: parameterAttributes?.required
          ? yup
              .string()
              .required(`${parameterAttributes?.headerText} is required`)
          : yup
              .string()
              .typeError(`${parameterAttributes?.headerText} should be string`),
      };
    }, {}) || {}
  );

  const handleSave = async (formValues: Record<string, unknown>) => {
    const parameterKeys = joinedPluginAuthenticationPluginParameters?.map(
      (parameter) => parameter.parameterAttributes.key
    );

    const formData: Record<string, unknown> = {};

    parameterKeys?.forEach((key) => {
      formData[key] = formValues[key];
    });

    const authenticationConnectionData = {
      pluginAuthenticationID: pluginAuthenticationID,
      description: 'Test Plugin Connection Authorization',
      name: 'test',
      parameters: parameterKeys?.length ? formData : null,
    };

    const pluginConfigureConnectionData = {
      parameters: parameterKeys?.length ? formData : null,
      pluginConnectionID: pluginConnection?.id,
      ...(!pluginConnection?.id && {
        name: 'new_connection',
        description: 'new_connection',
      }),
      pluginAuthenticationID: pluginAuthenticationID,
    };

    formValuesRef.current['pluginConfigureConnectionData'] =
      pluginConfigureConnectionData;

    formValuesRef.current['authenticationConnectionData'] =
      authenticationConnectionData;

    try {
      await authorizePluginAuthenticationConnection(
        authenticationConnectionData
      );
      const pluginConnectionResponse = await configurePluginConnection(
        pluginConfigureConnectionData
      );
      const { data: newPluginConnection } = pluginConnectionResponse;
      if (
        !checkConnectionValidity(
          newPluginConnection?.attributes?.pluginConnectionStatusTypeID
        )
      ) {
        setConnectionError({ message: 'Connection not authorized' });
        return;
      }
      if (!pluginConnection?.id) {
        onCreatePluginConnection(newPluginConnection.id);
      }

      onClose();
    } catch (error) {}
  };

  const handleRetry = async () => {
    setConnectionError(null);
    setShowError(false);
    removePluginAuthParameter();
    if (joinedPluginAuthenticationPluginParameters?.length === 0) {
      refetchConfigureConnection();
      refetchAuthorizeConnection();
      return;
    }
    resetAuthorizeConnect();
    resetConfigureConnection();
  };

  const isAutoConnectionLoading =
    isAutoAuthorizeConnectionLoading || isAutoConfigureConnectionLoading;

  const error: Error = (authenticationParameterError ||
    autoConfigureConnectionError ||
    autoAuthorizeConnectionError ||
    configureConnectionError ||
    authorizeConnectionError ||
    connectionError) as Error;

  if (error) {
    return (
      <PluginAuthenticationModalContainer isOpen={isOpen} onClose={onClose}>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          flexDirection='column'
          gap={2}
        >
          <ErrorStatusIcon width={100} height={100} />
          <Typography variant='h5' fontFamily='Mont-Semibold'>
            Error
          </Typography>
          <Typography>
            The connection failed.&nbsp;
            <Typography
              component='span'
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => setShowError((pre) => !pre)}
            >
              {showError ? 'Hide details' : 'Error details'}
            </Typography>
          </Typography>
          {showError && (
            <Typography component='span' textAlign='center'>
              {error?.message}
            </Typography>
          )}
          <Box display='flex' gap={2}>
            <SparkButton
              variant='outlined'
              sx={{ padding: '10px 20px' }}
              onClick={onClose}
            >
              Cancel
            </SparkButton>
            <SparkButton
              variant='contained'
              sx={{ padding: '10px 20px' }}
              onClick={handleRetry}
              disabled={
                isAuthorizeConnectionConnectionLoading ||
                isConnectionConfigureLoading
              }
            >
              Retry
            </SparkButton>
          </Box>
        </Box>
      </PluginAuthenticationModalContainer>
    );
  }

  if (
    (isAutoConnectionLoading &&
      !joinedPluginAuthenticationPluginParameters?.length) ||
    isAuthenticationParameterLoading
  ) {
    return (
      <PluginAuthenticationModalContainer isOpen={isOpen} onClose={onClose}>
        <Box
          display='flex'
          alignItems='center'
          flexDirection='column'
          gap={1}
          height='150px'
        >
          <Lottie options={defaultOptions} height={100} width={100} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        </Box>
      </PluginAuthenticationModalContainer>
    );
  }

  return (
    <PluginAuthenticationModalContainer isOpen={isOpen} onClose={onClose}>
      <>
        <Box
          width='60%'
          display='flex'
          flexDirection='column'
          gap={3}
          alignItems='center'
        >
          {selectedPlugins?.attributes.logoURL ? (
            <Box
              component='img'
              width='120px'
              height='120px'
              src={selectedPlugins?.attributes.logoURL || ''}
            />
          ) : (
            <WorkSpaceIcon width='120px' height='120px' />
          )}
          <Typography
            textAlign='center'
            fontSize='24px'
            fontFamily='Mont-semibold'
          >
            Allow SparkUp access to {selectedPlugins?.attributes.name}
          </Typography>
        </Box>
        <SparkForm
          validationSchema={validationSchema}
          initialValues={initialFormValue}
          onSave={handleSave}
          sx={{ paddingX: 2, paddingY: 1.5, justifyContent: 'center' }}
        >
          <>
            {joinedPluginAuthenticationPluginParameters?.map(
              (pluginAuthParameter) => {
                const { parameterAttributes } = pluginAuthParameter || {};

                const {
                  enumValues,
                  placeholderText,
                  uiComponentID,
                  subHeaderText,
                  headerText,
                  helpText,
                  sliderValues,
                } = parameterAttributes || {};

                const optionEnumValues = parseJSONCleaned<EnumValues>(
                  enumValues || ''
                );
                const parsedSliderValues = parseJSONCleaned<SliderValues>(
                  sliderValues || ''
                );
                const sliderOptions = parsedSliderValues?.sliderOptions;

                const _sliderOptions: SliderOptions = {
                  min: Number(sliderOptions?.min),
                  max: Number(sliderOptions?.max),
                  step: Number(sliderOptions?.stepSize),
                  decimalPrecision: Number(sliderOptions?.decimalPrecision),
                  renderPercentage: !!sliderOptions?.renderPercentage,
                };
                const options: DropdownOption[] =
                  optionEnumValues?.options.map((option) => ({
                    value: option.value,
                    label: option.title,
                    iconUrl: option.iconURL,
                    description: option.description,
                  })) || [];

                return (
                  <DynamicUIComponentFieldController
                    name={pluginAuthParameter.parameterAttributes.key}
                    helpText={helpText}
                    label={headerText}
                    placeholder={placeholderText}
                    subHeaderText={subHeaderText}
                    uiComponentID={uiComponentID}
                    sliderOptions={_sliderOptions}
                    key={pluginAuthParameter.id}
                    options={options}
                  />
                );
              }
            )}
            <Box display='flex' justifyContent='center' gap={4} width='60%'>
              <SparkBasicFormFooter
                onResetButtonClick={onClose}
                resetButtonText='Cancel'
                isLoading={
                  isAuthorizeConnectionConnectionLoading ||
                  isConnectionConfigureLoading
                }
                shouldRenderResetButton={true}
                resetButtonType='button'
                primaryButtonText='Yes, allow access'
                containerProps={{
                  flexDirection: 'row-reverse',
                }}
              />
            </Box>
          </>
        </SparkForm>
      </>
    </PluginAuthenticationModalContainer>
  );
}

export default PluginAuthenticationModal;

function PluginAuthenticationModalContainer({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <SparkDialog title='' hideTitle={true} open={isOpen} onClose={onClose}>
      <Box display='flex' flexDirection='column' padding={4}>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='100%'
          flexDirection='column'
          width='100%'
        >
          {children}
        </Box>
      </Box>
    </SparkDialog>
  );
}
